import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Message from '@mui/icons-material/Message';
import Drafts from '@mui/icons-material/Drafts';

// core
import { withCatch } from '_core/hocs/withCatch';
import { withLang } from '_core/hocs/withLang';

import { useGlobal } from '_core/hooks/useGlobal';

import { withVisitorInfo } from '_core/utils/visitorInfo';

import { Menu } from '_core/components/Menu';
import { Loading } from '_core/components/Loading';
import { MessagesSplashView } from '_core/views/MessagesSplash';
import { ModalOuter } from '_core/components/ModalOuter';
import NoteView from '_core/components/NoteView';

import {
  loadMessage,
  loadTopic,
  pickConversationTopicId
} from '_core/views/MessagesSplash/util.js';

// style
import { useStyles } from './headerControlsStyle';

const RawMessageNotifier = (props) => {
  const { visitorInfo, getLangString: l } = props;

  const classes = useStyles();

  const {
    request: { queue },
    config: { API_METHOD, API_NOTICE }
  } = useGlobal();

  const [state, setState] = useState({
    topicId: null,
    selectedMessageId: null,
    topicSubject: null,
    isOpen: false
  });
  const [unreadList, setUnreadList] = useState(null);

  const visitorInfoRef = React.useRef(visitorInfo);
  visitorInfoRef.current = visitorInfo;

  const queueRef = React.useRef(queue);
  queueRef.current = queue;

  const loadUnread = React.useCallback(() => {
    if (window.location.pathname === '/login') {
      return;
    }

    queueRef.current(API_METHOD.VISITOR_MESSAGE_LIST_UNREAD, {}).then((result) => {
      if (!result || !result.list) {
        return;
      }

      const data = result.list.map((x) => {
        const rawMessage = x.message || x;
        const topic = loadTopic(x.topic || rawMessage.topic);
        const message = loadMessage(rawMessage);
        const messageId = rawMessage.message_id ?? rawMessage.id ?? x.message_id ?? x.id ?? message.id;
        const conversationId = pickConversationTopicId(x, messageId);

        return {
          ...message,
          id: messageId,
          topic: {
            ...topic,
            id: conversationId
          }
        };
      }).filter((item) => item.id != null);

      setUnreadList(data);
      visitorInfoRef.current.unreadMessagesInit(data);
    }).catch(() => {});
  }, [API_METHOD.VISITOR_MESSAGE_LIST_UNREAD]);

  const loadUnreadRef = React.useRef(loadUnread);
  loadUnreadRef.current = loadUnread;

  React.useEffect(() => {
    loadUnreadRef.current();
  }, []);

  React.useEffect(() => {
    const notifyListener = (event) => {
      const name = event.detail?.name;

      if (
        name === API_NOTICE.VISITOR_MESSAGE_CREATE ||
        (typeof name === 'string' && name.indexOf('message/create') !== -1)
      ) {
        loadUnreadRef.current();
      } else if (name === API_NOTICE.VISITOR_MESSAGE_HAD_READ) {
        const messageIds = event.detail?.data?.message_ids;
        if (messageIds) {
          visitorInfoRef.current.messagesHadRead(messageIds);
          setUnreadList((prev) => {
            if (prev == null) {
              return prev;
            }

            const readIds = new Set(messageIds.map(String));
            return prev.filter((item) => !readIds.has(String(item.id)));
          });
        }
      }
    };

    window.addEventListener('notify', notifyListener);

    return () => window.removeEventListener('notify', notifyListener);
  }, [API_NOTICE.VISITOR_MESSAGE_CREATE, API_NOTICE.VISITOR_MESSAGE_HAD_READ]);

  const unreadMessages = visitorInfo.unreadMessages ?? unreadList;
  const unreadCount = unreadMessages?.length ?? 0;

  const renderButton = (btnProps) => (
    <IconButton {...btnProps}>
      <Badge
        classes={{ badge: classes.userNotifierBadge }}
        color="primary"
        badgeContent={unreadCount}
        max={99}
      >
        <Message />
      </Badge>
    </IconButton>
  );

  const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });

  const markMessagesRead = (messageIds) => {
    if (!messageIds.length) {
      return;
    }

    visitorInfo.messagesHadRead(messageIds);
    setUnreadList((prev) => {
      if (prev == null) {
        return prev;
      }

      const readIds = new Set(messageIds.map(String));
      return prev.filter((item) => !readIds.has(String(item.id)));
    });

    queue(
      API_METHOD.VISITOR_MESSAGE_HAD_READ,
      { message_ids: messageIds },
      RawMessageNotifier.displayName
    ).catch(() => {});
  };

  const renderItem = ({ onPopoverClose }, item) => {
    const onClick = () => {
      markMessagesRead([item.id]);
      onPopoverClose();
      setState({
        topicId: item.topic?.id ?? null,
        selectedMessageId: item.id,
        topicSubject: item.topic?.subject || null,
        isOpen: true
      });
    };

    const timestamp = new Date(item.timestamp);

    return (
      <ListItem
        button
        key={item.id}
        alignItems="flex-start"
        onClick={onClick}
      >
        <ListItemIcon>
          <Drafts />
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <div
              className={classes.userNotifierListItemPrimary}
              onClick={(event) => {
                if (event.target.closest('a')) {
                  event.stopPropagation();
                }
              }}
            >
              <NoteView>{item.content}</NoteView>
            </div>
          }
          secondary={
            <Typography variant="body2" color="text.secondary">
              {dateTimeFormat.format(timestamp)}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  const renderContent = (props) => (
    <List
      component="nav"
      subheader={
        <ListSubheader component="div">
          {l('r._.header.messenger')}

          {!unreadMessages ? <Loading /> : null}
        </ListSubheader>
      }
    >
      {unreadMessages != null ? (
        unreadMessages.length ? (
          unreadMessages.map((item) => renderItem(props, item))
        ) : (
          <ListItem
            button
            onClick={() => {
              const { onPopoverClose } = props;

              onPopoverClose();
              setState({
                topicId: null,
                selectedMessageId: null,
                topicSubject: null,
                isOpen: true
              });
            }}
          >
            <ListItemText secondary={l('r._.header.messenger.button.title')} />
          </ListItem>
        )
      ) : null}
    </List>
  );

  const onClose = () => {
    setState((prev) => {
      return { ...prev, ...{ isOpen: false } };
    });
  };

  return (
    <>
      <Menu
        ButtonComponent={renderButton}
        renderContent={renderContent}
        tooltip={l('r._.header.messenger')}
        paperProps={{ className: classes.userNotifierPaper }}
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }}
      />
      <ModalOuter
        open={state.isOpen}
        closeIcon={true}
        onClose={onClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        {state.isOpen ? (
          <MessagesSplashView
            key={`${state.topicId ?? 'topic'}:${state.selectedMessageId ?? 'message'}`}
            topicId={state.topicId}
            selectedMessageId={state.selectedMessageId}
            topicSubject={state.topicSubject}
          />
        ) : null}
      </ModalOuter>
    </>
  );
};

RawMessageNotifier.defaultProps = {};

RawMessageNotifier.propTypes = {
  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const MessageNotifier = withCatch(
  withVisitorInfo(withLang(RawMessageNotifier))
);
